import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Check, Share2, Settings, Clock, Navigation, Award } from 'lucide-react';

// ============================================
// STATIC DATA — defined outside component so
// useEffect and QR handler can reference it
// without dependency array issues
// ============================================
const colors = {
  magenta: '#C93AA3', purple: '#5E5BA5', cyan: '#00BCD4',
  green: '#00A550', gold: '#d4af37', teal: '#20B2AA'
};

const LOCATIONS = [
  {
    id: 1, name: 'Gotahold Brewing', type: 'brewery',
    address: '409 W. Van Buren, Eureka Springs, AR 72632',
    hours: 'Mon/Tue/Thu: 3-8pm, Fri: 2-9pm, Sat: 12-9pm, Sun: 12-8pm',
    lat: 36.40376, lng: -93.75464, color: colors.magenta,
    logo: '/images/gotahold_logo.png', checkInCode: 'GH2025',
    social: { instagram: '@gotaholdbrewing', facebook: '@gotaholdbrewing' }
  },
  {
    id: 2, name: 'Eureka Springs Brewery', type: 'brewery',
    address: '96 Ridgeview Rd, Eureka Springs, AR 72632',
    hours: 'Mon-Wed: 3-8pm, Thu: Closed, Fri-Sun: 12-8pm',
    lat: 36.4065, lng: -93.7405, color: colors.purple,
    logo: '/images/eureka_springs_brewery_logo.jpeg', checkInCode: 'ESB2025',
    social: { instagram: '@eurekasprings_brewery', facebook: 'facebook.com/profile.php?id=61580419926939' }
  },
  {
    id: 3, name: 'Lovell Estate & Winery', type: 'winery',
    address: '115 W Van Buren, Eureka Springs, AR 72632',
    hours: 'Thu: 12-5pm, Fri-Sat: 12-7pm, Sun: 12-5pm, Mon-Wed: Closed',
    lat: 36.39394, lng: -93.74307, color: colors.cyan,
    logo: '/images/lovell_estates_logo.png', checkInCode: 'LVL2025',
    social: { facebook: 'facebook.com/profile.php?id=61576399405383' }
  },
  {
    id: 4, name: 'Railway Winery', type: 'winery',
    address: '4937 State Hwy 187, Eureka Springs, AR 72631',
    hours: 'Wed-Sun: 12-5pm',
    lat: 36.4786437, lng: -93.8023733, color: colors.green,
    logo: '/images/railway_winery_logo.jpg', checkInCode: 'RWY2025',
    social: { instagram: '@RailwayWinery', facebook: '@RailwayWinery' }
  },
  {
    id: 5, name: 'Butler Creek Distilling', type: 'distillery',
    address: '4937 State Hwy 187, Eureka Springs, AR 72631',
    hours: 'Wed-Sun: 12-5pm',
    lat: 36.4786437, lng: -93.8023733, color: colors.teal,
    logo: '/images/butler_creek_logo.jpg', checkInCode: 'BCD2025',
    social: { facebook: 'facebook.com/profile.php?id=100057312855092' }
  }
];

const APP_URL = 'https://eurekaspringstastingtrail.com';
const AIRTABLE_BASE_ID = 'appvEPncH8hEbguTI';

// ============================================
// MAIN COMPONENT
// ============================================
const TastingTrailApp = () => {

  const AIRTABLE_API_KEY = process.env.REACT_APP_AIRTABLE_API_KEY;
  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'trail2025';

  const [currentView, setCurrentView] = useState('trail');
  const [checkIns, setCheckIns] = useState([]);
  const [showCompletion, setShowCompletion] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({ name: '', email: '', phone: '', birthdate: '', marketingConsent: false });
  const [showCheckInConfirmation, setShowCheckInConfirmation] = useState(false);
  const [pendingCheckIn, setPendingCheckIn] = useState(null);
  const [checkInConfirmed, setCheckInConfirmed] = useState({ purchase: false, follow: false, photo: false });
  const [pendingQRLocation, setPendingQRLocation] = useState(null);
  const [showQRInstructions, setShowQRInstructions] = useState(false);
  const [qrInstructionLocation, setQRInstructionLocation] = useState(null);
  const [pendingGPSCheckIn, setPendingGPSCheckIn] = useState(null);
  const [logoErrors, setLogoErrors] = useState({});
  // NEW: for manual reset in admin panel
  const [resetEmail, setResetEmail] = useState('');

  const locationRef = useRef(null);

  // ── LOCALSTORAGE ──
  const saveUserInfo = (info) => { try { window.localStorage.setItem('tastingTrailUser', JSON.stringify(info)); } catch (e) {} };
  const loadUserInfo = () => { try { const s = window.localStorage.getItem('tastingTrailUser'); return s ? JSON.parse(s) : null; } catch (e) { return null; } };
  const saveCheckIns = (d) => { try { window.localStorage.setItem('tastingTrailCheckIns', JSON.stringify(d)); } catch (e) {} };

  // ── LOAD CHECK-INS with auto-reset for prior-month completers ──
  const loadCheckIns = () => {
    try {
      const s = window.localStorage.getItem('tastingTrailCheckIns');
      if (!s) return [];
      const parsed = JSON.parse(s);

      // Auto-reset if: completed all 5 AND today is after the 3rd of the month
      const today = new Date();
      if (parsed.length >= 5 && today.getDate() > 3) {
        const lastCheckIn = new Date(parsed[parsed.length - 1].timestamp);
        const priorMonth =
          lastCheckIn.getMonth() !== today.getMonth() ||
          lastCheckIn.getFullYear() !== today.getFullYear();
        if (priorMonth) {
          window.localStorage.removeItem('tastingTrailCheckIns');
          return [];
        }
      }

      return parsed;
    } catch (e) { return []; }
  };

  // ── AIRTABLE ──
  const sendToAirtable = async (tableName, fields) => {
    if (!AIRTABLE_API_KEY) {
      console.warn('⏭️ REACT_APP_AIRTABLE_API_KEY not set — skipping Airtable sync');
      return false;
    }
    try {
      const r = await fetch(`https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${tableName}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${AIRTABLE_API_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields })
      });
      if (!r.ok) {
        const errText = await r.text();
        console.error(`❌ Airtable error (${tableName}):`, errText);
        return false;
      }
      return true;
    } catch (e) {
      console.error('❌ Airtable network error:', e);
      return false;
    }
  };

  const syncToAirtable = async (type, data) => {
    if (type === 'registration') return sendToAirtable('Registrations', { 'Name': data.name, 'Email': data.email, 'Phone': data.phone, 'Birthdate': data.birthdate, 'Marketing Consent': data.marketingConsent });
    if (type === 'checkin') return sendToAirtable('Check-ins', { 'User Name': data.userName, 'Email': data.userEmail, 'Location Name': data.locationName, 'Location ID': parseInt(data.locationId), 'Check-in Method': data.method });
  };

  // ── REMOTE RESET: check Airtable for a reset flag on this user's email ──
  const checkForRemoteReset = async (email) => {
    if (!AIRTABLE_API_KEY || !email) return false;
    try {
      const url = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/Resets?filterByFormula=${encodeURIComponent(`{Email}="${email}"`)}`;
      const r = await fetch(url, { headers: { 'Authorization': `Bearer ${AIRTABLE_API_KEY}` } });
      if (!r.ok) return false;
      const data = await r.json();
      if (data.records && data.records.length > 0) {
        // Delete the reset flag(s) from Airtable
        for (const record of data.records) {
          await fetch(`https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/Resets/${record.id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${AIRTABLE_API_KEY}` }
          });
        }
        // Clear local check-ins silently
        window.localStorage.removeItem('tastingTrailCheckIns');
        return true;
      }
    } catch (e) {
      console.error('❌ Remote reset check failed:', e);
    }
    return false;
  };

  // ── UTILS ──
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959, dLat = (lat2-lat1)*Math.PI/180, dLon = (lon2-lon1)*Math.PI/180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const isCheckedIn = (id) => checkIns.some(c => c.locationId === id);

  const getQRImageUrl = (location) => {
    const url = `${APP_URL}?checkin=${location.checkInCode}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(url)}&bgcolor=2a1810&color=f5e6d3&margin=10`;
  };

  // ── GPS ──
  const getCurrentLocation = () => {
    if (!navigator.geolocation) { alert('Geolocation is not supported by your browser.'); return; }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy };
        locationRef.current = loc;
        setUserLocation(loc);
        setLocationLoading(false);
      },
      () => { setLocationLoading(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // When GPS arrives, auto-retry any pending GPS check-in
  useEffect(() => {
    if (userLocation && pendingGPSCheckIn) {
      const loc = pendingGPSCheckIn;
      setPendingGPSCheckIn(null);
      handleGPSCheckIn(loc, userLocation);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation]);

  // ── INIT ──
  useEffect(() => {
    const loadedUser = loadUserInfo();
    if (loadedUser) {
      setUserInfo(loadedUser);
      setShowRegistration(false);
      // Check Airtable for a remote reset flag before loading check-ins
      checkForRemoteReset(loadedUser.email).then(wasReset => {
        const savedCheckIns = wasReset ? [] : loadCheckIns();
        setCheckIns(savedCheckIns);
      });
    } else {
      setShowRegistration(true);
      setCheckIns(loadCheckIns());
    }
    getCurrentLocation();

    const params = new URLSearchParams(window.location.search);
    const code = params.get('checkin');
    if (code) {
      const location = LOCATIONS.find(l => l.checkInCode === code);
      if (location) {
        window.history.replaceState({}, '', window.location.pathname);
        const saved = loadCheckIns();
        if (saved.some(c => c.locationId === location.id)) {
          alert(`You've already checked in at ${location.name}!`);
          return;
        }
        const savedUser = loadUserInfo();
        if (savedUser) {
          setPendingCheckIn({ ...location, checkInMethod: 'QR' });
          setShowCheckInConfirmation(true);
        } else {
          setPendingQRLocation(location);
          setShowRegistration(true);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── REGISTRATION ──
  const handleRegistrationSubmit = () => {
    if (!registrationForm.name || !registrationForm.email || !registrationForm.phone || !registrationForm.birthdate) {
      alert('Please fill in all required fields'); return;
    }
    const bd = new Date(registrationForm.birthdate), today = new Date();
    let age = today.getFullYear() - bd.getFullYear();
    const m = today.getMonth() - bd.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
    if (age < 21) { alert('You must be 21 or older to participate'); return; }
    if (!registrationForm.marketingConsent) { alert('Please agree to marketing messages to participate in drawings'); return; }
    setUserInfo(registrationForm);
    saveUserInfo(registrationForm);
    setShowRegistration(false);
    syncToAirtable('registration', registrationForm).catch(() => {});
    if (pendingQRLocation) {
      setPendingCheckIn({ ...pendingQRLocation, checkInMethod: 'QR' });
      setShowCheckInConfirmation(true);
      setPendingQRLocation(null);
    }
  };

  // ── GPS CHECK-IN LOGIC ──
  const showQRFallback = (location, distanceFeet = null) => {
    setQRInstructionLocation({ ...location, distanceFeet });
    setShowQRInstructions(true);
  };

  const handleGPSCheckIn = (location, loc) => {
    const dist = calculateDistance(loc.latitude, loc.longitude, location.lat, location.lng);
    if (dist <= 0.0947) {
      setPendingCheckIn({ ...location, checkInMethod: 'GPS' });
      setShowCheckInConfirmation(true);
    } else {
      showQRFallback(location, Math.round(dist * 5280));
    }
  };

  const handleCheckIn = (location) => {
    if (!userInfo) { alert('Please register first'); setShowRegistration(true); return; }
    if (isCheckedIn(location.id)) return;
    const loc = locationRef.current;
    if (!loc) {
      setPendingGPSCheckIn(location);
      getCurrentLocation();
      showQRFallback(location);
      return;
    }
    handleGPSCheckIn(location, loc);
  };

  // ── COMPLETE CHECK-IN ──
  const handleCheckInComplete = () => {
    if (!checkInConfirmed.purchase || !checkInConfirmed.follow || !checkInConfirmed.photo) {
      alert('Please confirm all three requirements'); return;
    }
    const newCheckIn = {
      locationId: pendingCheckIn.id, timestamp: new Date().toISOString(),
      userName: userInfo.name, userEmail: userInfo.email,
      locationName: pendingCheckIn.name, method: pendingCheckIn.checkInMethod
    };
    const updated = [...checkIns, newCheckIn];
    setCheckIns(updated);
    saveCheckIns(updated);
    setShowCheckInConfirmation(false);
    setPendingCheckIn(null);
    setCheckInConfirmed({ purchase: false, follow: false, photo: false });
    if (updated.length === LOCATIONS.length) setShowCompletion(true);
    syncToAirtable('checkin', { userName: userInfo.name, userEmail: userInfo.email, locationName: newCheckIn.locationName, locationId: newCheckIn.locationId, method: newCheckIn.method }).catch(() => {});
  };

  // ── SHARE ──
  const handleShare = async () => {
    const txt = `I'm exploring the Eureka Springs Tasting Trail! ${checkIns.length}/5 locations visited. Join me at eurekaspringstastingtrail.com`;
    if (navigator.share) { try { await navigator.share({ text: txt }); } catch {} }
    else { try { await navigator.clipboard.writeText(txt); alert('Link copied!'); } catch { alert(txt); } }
  };

  // ── ADMIN ──
  const handleAdminLogin = () => {
    if (adminPassword === ADMIN_PASSWORD) { setIsAdmin(true); setShowAdminLogin(false); setCurrentView('admin'); }
    else alert('Incorrect password');
  };

  const handleResetProgress = () => {
    if (window.confirm('Reset all progress? This cannot be undone.')) {
      setCheckIns([]); setUserInfo(null);
      window.localStorage.removeItem('tastingTrailUser');
      window.localStorage.removeItem('tastingTrailCheckIns');
      setShowRegistration(true); setCurrentView('trail'); setIsAdmin(false);
    }
  };

  // ── MANUAL + REMOTE RESET ──
  const handleManualReset = async () => {
    if (!resetEmail.trim()) { alert('Please enter an email address'); return; }
    const email = resetEmail.trim().toLowerCase();

    // If it's the current device's user, reset locally right now
    if (userInfo && userInfo.email.toLowerCase() === email) {
      window.localStorage.removeItem('tastingTrailCheckIns');
      setCheckIns([]);
      setResetEmail('');
      alert(`✅ Check-ins cleared for ${email}`);
      return;
    }

    // Otherwise write a remote reset flag to Airtable
    if (!AIRTABLE_API_KEY) {
      alert('Airtable not configured — cannot do remote reset');
      return;
    }
    try {
      const r = await fetch(`https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/Resets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fields: { 'Email': email, 'Requested At': new Date().toISOString() } })
      });
      if (r.ok) {
        setResetEmail('');
        alert(`✅ Reset flagged for ${email}. Their check-ins will clear next time they open the app.`);
      } else {
        alert('❌ Failed to write reset flag. Check Airtable connection.');
      }
    } catch (e) {
      alert('❌ Network error writing reset flag.');
    }
  };

  const inpStyle = (extra = {}) => ({
    width: '100%', padding: '0.75rem', borderRadius: '8px',
    border: `2px solid ${colors.cyan}`, background: 'rgba(0,188,212,0.1)',
    color: '#f5e6d3', fontSize: '1rem', fontFamily: "'Lora',serif", ...extra
  });

  // Logo fallback using React state
  const LogoCircle = ({ loc }) => {
    const fallbackEmoji = loc.type === 'brewery' ? '🍺' : loc.type === 'winery' ? '🍷' : '🥃';
    return (
      <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: loc.color, margin: '0 auto 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: `3px solid ${loc.color}` }}>
        {loc.logo && !logoErrors[loc.id] ? (
          <img src={loc.logo} alt={loc.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setLogoErrors(prev => ({ ...prev, [loc.id]: true }))} />
        ) : (
          <span style={{ fontSize: '2rem' }}>{fallbackEmoji}</span>
        )}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1a1410 0%,#2a1f1a 100%)', color: '#f5e6d3', fontFamily: "'Lora',serif" }}>

      {/* ── REGISTRATION MODAL ── */}
      {showRegistration && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem', overflowY: 'auto' }}>
          <div style={{ background: 'linear-gradient(135deg,#2a1810,#1a0f0a)', padding: '2rem', borderRadius: '16px', maxWidth: '480px', width: '100%', border: `2px solid ${colors.cyan}` }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.8rem', color: colors.cyan }}>Join the Tasting Trail</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', fontWeight: 'bold', color: colors.gold }}>Must be 21 years or older to participate</p>
            {pendingQRLocation && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: `${colors.green}20`, border: `2px solid ${colors.green}`, borderRadius: '8px' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: colors.green }}>✅ QR code scanned at <strong>{pendingQRLocation.name}</strong> — register below to complete your check-in!</p>
              </div>
            )}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: `${colors.gold}20`, border: `2px solid ${colors.gold}`, borderRadius: '8px' }}>
              <p style={{ margin: '0 0 0.5rem', fontWeight: 'bold', color: colors.gold }}>🎁 Monthly $25 Gift Certificate Drawing!</p>
              <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.5 }}>Complete all 5 check-ins and be entered to win a $25 gift certificate. Winners drawn monthly!</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[['Name','name','text'],['Email','email','email'],['Phone','phone','tel'],['Birthdate (must be 21+)','birthdate','date']].map(([label,key,type]) => (
                <div key={key}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>{label} <span style={{ color: colors.magenta }}>*</span></label>
                  <input type={type} value={registrationForm[key]} onChange={e => setRegistrationForm({ ...registrationForm, [key]: e.target.value })} style={inpStyle()} />
                </div>
              ))}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={registrationForm.marketingConsent} onChange={e => setRegistrationForm({ ...registrationForm, marketingConsent: e.target.checked })} style={{ marginTop: '0.25rem' }} />
                <span style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>I agree to receive marketing messages from Eureka Springs Tasting Trail participating businesses. <span style={{ color: colors.magenta }}>*Required for monthly drawings.</span></span>
              </label>
              <button onClick={handleRegistrationSubmit} style={{ padding: '1rem', background: `linear-gradient(135deg,${colors.magenta},${colors.purple})`, color: '#f5e6d3', border: 'none', borderRadius: '12px', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer' }}>
                Start Your Trail Adventure
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── QR FALLBACK MODAL ── */}
      {showQRInstructions && qrInstructionLocation && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ background: 'linear-gradient(135deg,#2a1810,#1a0f0a)', padding: '2rem', borderRadius: '16px', maxWidth: '420px', width: '100%', border: `2px solid ${qrInstructionLocation.color}`, textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 0.5rem', color: qrInstructionLocation.color, fontSize: '1.4rem' }}>Scan to Check In</h3>
            <h4 style={{ margin: '0 0 1rem', color: colors.gold, fontSize: '1rem', fontWeight: 'normal' }}>{qrInstructionLocation.name}</h4>
            {qrInstructionLocation.distanceFeet && (
              <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', opacity: 0.7 }}>
                📍 GPS shows you're {qrInstructionLocation.distanceFeet} feet away — use the QR code instead.
              </p>
            )}
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem', border: `1px solid ${qrInstructionLocation.color}40` }}>
              <p style={{ margin: '0 0 1.25rem', fontSize: '1rem', lineHeight: 1.6 }}>
                Look for the <strong style={{ color: qrInstructionLocation.color }}>QR code sign</strong> posted at this location and scan it with your phone camera.
              </p>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📷</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', textAlign: 'left', fontSize: '0.9rem', opacity: 0.85 }}>
                {[['Open your phone\'s camera app'],['Point it at the QR code posted here'],['Tap the link that appears — you\'re in!']].map(([step], i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ background: qrInstructionLocation.color, borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.75rem', fontWeight: 'bold', color: '#1a1410' }}>{i+1}</span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={() => { setShowQRInstructions(false); setQRInstructionLocation(null); }}
              style={{ width: '100%', padding: '1rem', background: 'rgba(255,255,255,0.1)', color: '#f5e6d3', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', cursor: 'pointer', fontSize: '1rem' }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── CHECK-IN CONFIRMATION MODAL ── */}
      {showCheckInConfirmation && pendingCheckIn && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ background: 'linear-gradient(135deg,#2a1810,#1a0f0a)', padding: '2rem', borderRadius: '16px', maxWidth: '480px', width: '100%', border: `2px solid ${pendingCheckIn.color}` }}>
            <h3 style={{ margin: '0 0 0.5rem', color: pendingCheckIn.color, fontSize: '1.5rem' }}>Check in at {pendingCheckIn.name}</h3>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', opacity: 0.7 }}>
              {pendingCheckIn.checkInMethod === 'QR' ? '✅ Checked in via QR code' : '📍 Checked in via GPS'}
            </p>
            <p style={{ marginBottom: '1.5rem', opacity: 0.9 }}>Please confirm you've completed the following:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
              {[['purchase','✓ Made a purchase at this location'],['follow','✓ Following on social media'],['photo','✓ Took a photo of your purchase and tagged the business']].map(([key,label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={checkInConfirmed[key]} onChange={e => setCheckInConfirmed({ ...checkInConfirmed, [key]: e.target.checked })} style={{ width: '20px', height: '20px' }} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={() => { setShowCheckInConfirmation(false); setPendingCheckIn(null); setCheckInConfirmed({ purchase: false, follow: false, photo: false }); }}
                style={{ flex: 1, padding: '1rem', background: 'rgba(255,255,255,0.1)', color: '#f5e6d3', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCheckInComplete} disabled={!checkInConfirmed.purchase || !checkInConfirmed.follow || !checkInConfirmed.photo}
                style={{ flex: 1, padding: '1rem', borderRadius: '12px', border: 'none', fontWeight: 'bold', color: '#f5e6d3', cursor: checkInConfirmed.purchase && checkInConfirmed.follow && checkInConfirmed.photo ? 'pointer' : 'not-allowed', opacity: checkInConfirmed.purchase && checkInConfirmed.follow && checkInConfirmed.photo ? 1 : 0.5, background: checkInConfirmed.purchase && checkInConfirmed.follow && checkInConfirmed.photo ? `linear-gradient(135deg,${pendingCheckIn.color},${colors.gold})` : 'rgba(255,255,255,0.1)' }}>
                Complete Check-In
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── COMPLETION MODAL ── */}
      {showCompletion && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: `linear-gradient(135deg,${colors.magenta},${colors.cyan})`, borderRadius: '24px', padding: '3rem', textAlign: 'center', maxWidth: '480px', border: `4px solid ${colors.gold}`, boxShadow: `0 0 40px ${colors.gold}80` }}>
            <Award size={80} style={{ color: colors.gold, marginBottom: '1rem' }} />
            <h2 style={{ fontSize: '2.5rem', margin: '0 0 1rem', color: colors.gold }}>Trail Complete!</h2>
            <p style={{ fontSize: '1.3rem', marginBottom: '1rem', fontWeight: 'bold', color: colors.gold }}>🎉 You're Entered in the Monthly Drawing! 🎉</p>
            <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>Congratulations! You've visited all 5 locations and are entered to win a <strong style={{ color: colors.gold }}>$25 gift certificate</strong>.</p>
            <p style={{ fontSize: '1rem', marginBottom: '2rem', lineHeight: 1.6, opacity: 0.95 }}>Winners are drawn monthly and notified via email and phone.</p>
            <button onClick={() => setShowCompletion(false)} style={{ padding: '1rem 2rem', background: colors.gold, border: 'none', borderRadius: '12px', color: '#1a1410', cursor: 'pointer', fontSize: '1.1rem', fontWeight: 'bold' }}>Awesome!</button>
          </div>
        </div>
      )}

      {/* ── HEADER ── */}
      <div style={{ background: 'linear-gradient(135deg,#2a1810,#1a0f0a)', padding: '1.5rem', borderBottom: `2px solid ${colors.gold}`, position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ margin: 0, fontSize: '1.8rem', background: `linear-gradient(135deg,${colors.magenta},${colors.cyan})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Eureka Springs Tasting Trail
          </h1>
          {userInfo && (
            <p style={{ margin: '0.5rem 0 0', opacity: 0.8, fontSize: '0.9rem' }}>
              Welcome back, {userInfo.name}!
              {locationLoading && <span style={{ marginLeft: '1rem', color: colors.cyan }}>📍 Getting your location...</span>}
              {!locationLoading && userLocation && <span style={{ marginLeft: '1rem', color: colors.green, fontSize: '0.8rem' }}>📍 Location found</span>}
            </p>
          )}
        </div>
      </div>

      {/* ── PROGRESS BAR ── */}
      {userInfo && (
        <div style={{ maxWidth: '1200px', margin: '1.5rem auto 0', padding: '0 1.5rem' }}>
          <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Progress: {checkIns.length} of 5 completed</span>
            <button onClick={handleShare} style={{ padding: '0.5rem 1rem', background: `linear-gradient(135deg,${colors.purple},${colors.cyan})`, border: 'none', borderRadius: '8px', color: '#f5e6d3', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Share2 size={16} /> Share
            </button>
          </div>
          <div style={{ width: '100%', height: '12px', background: 'rgba(255,255,255,0.1)', borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ width: `${(checkIns.length / LOCATIONS.length) * 100}%`, height: '100%', background: `linear-gradient(90deg,${colors.magenta},${colors.purple},${colors.cyan},${colors.green},${colors.gold})`, transition: 'width 0.5s ease', borderRadius: '6px' }} />
          </div>
        </div>
      )}

      {/* ── DRAWING BANNER ── */}
      {userInfo && checkIns.length < LOCATIONS.length && (
        <div style={{ maxWidth: '1200px', margin: '1.5rem auto 0', padding: '0 1.5rem' }}>
          <div style={{ padding: '1rem', background: `${colors.gold}20`, border: `2px solid ${colors.gold}`, borderRadius: '12px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>
              <strong style={{ color: colors.gold }}>🎁 Complete all 5 locations</strong> to enter our monthly drawing for a <strong style={{ color: colors.gold }}>$25 gift certificate!</strong>
            </p>
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT ── */}
      <div style={{ maxWidth: '1200px', margin: '1.5rem auto', padding: '0 1.5rem' }}>
        {currentView === 'trail' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '1.5rem' }}>
            {LOCATIONS.map(loc => {
              const checked = isCheckedIn(loc.id);
              return (
                <div key={loc.id} style={{ background: 'linear-gradient(135deg,#2a1810,#1a0f0a)', borderRadius: '16px', padding: '1.5rem', border: `2px solid ${checked ? colors.gold : loc.color}`, position: 'relative', boxShadow: checked ? `0 0 20px ${colors.gold}40` : 'none' }}>
                  {checked && (
                    <div style={{ position: 'absolute', top: '-10px', right: '-10px', background: colors.gold, borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Check size={24} color="#1a1410" strokeWidth={3} />
                    </div>
                  )}
                  <LogoCircle loc={loc} />
                  <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.3rem', color: loc.color, textAlign: 'center' }}>{loc.name}</h3>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <MapPin size={16} style={{ marginTop: '0.2rem', flexShrink: 0 }} /><span>{loc.address}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <Clock size={16} style={{ marginTop: '0.2rem', flexShrink: 0 }} /><span>{loc.hours}</span>
                    </div>
                  </div>
                  {!checked ? (
                    <>
                      <button onClick={() => handleCheckIn(loc)} style={{ width: '100%', padding: '1rem', background: `linear-gradient(135deg,${loc.color},${colors.gold})`, border: 'none', borderRadius: '12px', color: '#f5e6d3', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <Navigation size={20} /> Check In with GPS
                      </button>
                      <p style={{ margin: '0.75rem 0 0', textAlign: 'center', fontSize: '0.8rem', opacity: 0.6 }}>Or scan the QR code posted at this location</p>
                    </>
                  ) : (
                    <div style={{ width: '100%', padding: '1rem', background: colors.gold, borderRadius: '12px', textAlign: 'center', color: '#1a1410', fontSize: '1rem', fontWeight: 'bold' }}>✓ Checked In!</div>
                  )}
                </div>
              );
            })}
          </div>

        ) : (
          <div>
            {/* ── MAIN ADMIN PANEL ── */}
            <div style={{ background: 'linear-gradient(135deg,#2a1810,#1a0f0a)', borderRadius: '16px', padding: '2rem', border: `2px solid ${colors.purple}`, marginBottom: '2rem' }}>
              <h2 style={{ margin: '0 0 1.5rem', color: colors.purple }}>Admin Panel</h2>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button onClick={handleResetProgress} style={{ padding: '1rem 2rem', background: colors.magenta, border: 'none', borderRadius: '12px', color: '#f5e6d3', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }}>Reset All Progress</button>
                <button onClick={() => { setCurrentView('trail'); setIsAdmin(false); }} style={{ padding: '1rem 2rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', color: '#f5e6d3', fontSize: '1rem', cursor: 'pointer' }}>Back to Trail</button>
              </div>

              {/* ── MANUAL / REMOTE USER RESET ── */}
              <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: `1px solid ${colors.purple}` }}>
                <p style={{ margin: '0 0 0.5rem', color: colors.gold, fontWeight: 'bold', fontSize: '1rem' }}>🔄 Reset a User's Check-ins</p>
                <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', opacity: 0.7, lineHeight: 1.5 }}>
                  Enter a user's email address. If they're on this device, their check-ins clear immediately.
                  Otherwise a reset flag is sent — their check-ins will clear silently next time they open the app.
                  Note: users who completed all 5 locations before the 3rd of the month are reset automatically on the 4th.
                </p>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleManualReset()}
                  placeholder="user@email.com"
                  style={inpStyle({ border: `2px solid ${colors.purple}`, background: 'rgba(94,91,165,0.1)', marginBottom: '0.75rem', boxSizing: 'border-box' })}
                />
                <button onClick={handleManualReset} style={{ width: '100%', padding: '0.75rem', background: colors.purple, color: '#f5e6d3', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem' }}>
                  Reset This User
                </button>
              </div>

              {/* ── AIRTABLE TEST ── */}
              <div style={{ marginTop: '2rem', padding: '1.5rem', border: `2px dashed ${colors.gold}`, borderRadius: '12px' }}>
                <p style={{ margin: '0 0 1rem', color: colors.gold, fontWeight: 'bold' }}>🧪 Airtable Test (Admin Only)</p>
                <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', opacity: 0.7 }}>Sends a test check-in record to Airtable to confirm the connection is working. Check your Airtable Check-ins table after tapping.</p>
                <button onClick={async () => {
                  const key = process.env.REACT_APP_AIRTABLE_API_KEY;
                  if (!key) { alert('❌ API key is not loaded. The environment variable REACT_APP_AIRTABLE_API_KEY is missing from this build.'); return; }
                  alert(`🔑 Key found! Starts with: ${key.substring(0, 10)}... (${key.length} characters total). Now sending to Airtable...`);
                  try {
                    const r = await fetch(`https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/Check-ins`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${key}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ fields: { 'User Name': 'TEST USER', 'Email': 'test@test.com', 'Location Name': 'Gotahold Brewing', 'Location ID': 1, 'Check-in Method': 'Admin Test' } })
                    });
                    const txt = await r.text();
                    alert(r.ok ? '✅ Success! Check your Airtable Check-ins table.' : `❌ Airtable error ${r.status}: ${txt}`);
                  } catch(e) {
                    alert(`❌ Network error: ${e.message}`);
                  }
                }} style={{ padding: '0.75rem 1.5rem', background: colors.gold, border: 'none', borderRadius: '8px', color: '#1a1410', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }}>
                  Send Test Check-in
                </button>
              </div>
            </div>

            {/* ── QR CODES ── */}
            <div style={{ background: 'linear-gradient(135deg,#2a1810,#1a0f0a)', borderRadius: '16px', padding: '2rem', border: `2px solid ${colors.gold}` }}>
              <h2 style={{ margin: '0 0 0.5rem', color: colors.gold }}>📱 Location QR Codes</h2>
              <p style={{ margin: '0 0 2rem', opacity: 0.8, fontSize: '0.9rem' }}>Print these and post them at each location. Guests scan with their phone camera to check in — no GPS needed.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '1.5rem' }}>
                {LOCATIONS.map(loc => (
                  <div key={loc.id} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '1.5rem', border: `2px solid ${loc.color}`, textAlign: 'center' }}>
                    <h3 style={{ margin: '0 0 1rem', color: loc.color, fontSize: '1rem' }}>{loc.name}</h3>
                    <img src={getQRImageUrl(loc)} alt={`QR code for ${loc.name}`} style={{ width: '180px', height: '180px', borderRadius: '8px', border: `3px solid ${loc.color}` }} />
                    <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', opacity: 0.6 }}>Code: {loc.checkInCode}</p>
                  </div>
                ))}
              </div>
              <p style={{ margin: '1.5rem 0 0', fontSize: '0.85rem', opacity: 0.6, textAlign: 'center' }}>💡 Right-click any QR code → Save Image, then print at least 3"×3" for easy scanning.</p>
            </div>
          </div>
        )}
      </div>

      {/* ── ADMIN BUTTON ── */}
      {!isAdmin && (
        <button onClick={() => setShowAdminLogin(true)} style={{ position: 'fixed', bottom: '2rem', right: '2rem', width: '50px', height: '50px', borderRadius: '50%', background: colors.purple, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
          <Settings size={24} color="#f5e6d3" />
        </button>
      )}

      {/* ── ADMIN LOGIN MODAL ── */}
      {showAdminLogin && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'linear-gradient(135deg,#2a1810,#1a0f0a)', padding: '2rem', borderRadius: '16px', border: `2px solid ${colors.purple}`, minWidth: '300px' }}>
            <h3 style={{ margin: '0 0 1rem', color: colors.purple }}>Admin Login</h3>
            <input type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdminLogin()}
              placeholder="Enter password"
              style={inpStyle({ border: `2px solid ${colors.purple}`, background: 'rgba(94,91,165,0.1)', marginBottom: '1rem' })} />
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={() => { setShowAdminLogin(false); setAdminPassword(''); }} style={{ flex: 1, padding: '0.75rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#f5e6d3', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAdminLogin} style={{ flex: 1, padding: '0.75rem', background: colors.purple, border: 'none', borderRadius: '8px', color: '#f5e6d3', fontWeight: 'bold', cursor: 'pointer' }}>Login</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TastingTrailApp;
